import { AuthFailureEvent } from '../domain/enums/auth-failure-event';
import { ErrorCode } from '../domain/enums/error-code';
import { HttpHeaders } from '@angular/common/http';
import { isDevMode } from '@angular/core';
import { ApolloLink } from '@apollo/client';
import { getFormattedGraphqlError, onError } from '@ppl/utils/apollo.utils';
import { TIMEZONE_NAME_OR_OFFSET } from '@ppl/utils/date.utils';
import { getApolloGraphQLErrors } from '@ppl/utils/error.utils';
import { throwError } from 'rxjs';
import type { LinkInfo } from '@ppl/utils/apollo.utils';
import type { HttpLink } from 'apollo-angular/http';
import type { GraphQLError } from 'graphql';
import type { EMPTY } from 'rxjs';
import type { ApiError, ApiErrorBase, EntityValidationException, ErrorHandlerService } from '../service/error-handler.service';


export const getApolloLink = ({
  httpLink,
  gqlUrl,
  errorHandlerService,
  token = null,
  tokenPrefix = 'PipelinerToken',
  appId,
  customHeaders,
  createLinkChain,
  version
}: {
  httpLink: HttpLink;
  gqlUrl: string;
  errorHandlerService?: ErrorHandlerService;
  token?: string;
  tokenPrefix?: string;
  appId?: string;
  customHeaders?: { [key: string]: string; };
  createLinkChain?: (defaults: CreateLinkChainDefaults) => ApolloLink[];
  version?: string;
}) => {
  const errorLink = onError(({ networkError, graphQLErrors, response, operation }) => {
    const context: any = operation.getContext();
    const statusCode = networkError
      ? (networkError as any).status || (networkError as any).statusCode
      : null;

    const isUnauthorizedError = (statusCode === AuthFailureEvent.Unauthorized) || (statusCode === AuthFailureEvent.SessionExpiredByPolicy);
    if (isUnauthorizedError) {
      const shouldHandleUnauthorized = !(context && context.doNotHandleUnauthorized);
      if (shouldHandleUnauthorized) {
        errorHandlerService.handleUnauthorizedError(context, { statusCode });
      }
      return;
    }

    if (statusCode === AuthFailureEvent.AccessBlockedByIP) {
      errorHandlerService.handleAccessBlockedError();
      return;
    }

    if (graphQLErrors) {
      graphQLErrors.forEach((graphqlError: (GraphQLError & { api_error: any; })) => console.error(getFormattedGraphqlError(graphqlError)));
      console.log('[GraphQL Query]', operation.query.loc.source.body);
      console.log('[GraphQL Variables]', '\n' + JSON.stringify(operation.variables));
    }
    if (networkError) {
      console.log(`[Network error]: ${JSON.stringify(networkError)}`);
    }

    const traceId = context.response?.headers.get('X-Pipeliner-Trace-Id');
    if (traceId) {
      console.log(`[Pipeliner Trace Id]: ${traceId}`);
    }

    const error = context.error;
    const errorWhiteList = context && context.errorWhiteList;
    const logicalUserErrorsMessages = context && context.logicalUserErrorsMessages;
    if (errorHandlerService && error !== false) {
      errorHandlerService.handleApiError({
        networkError,
        graphQLErrors,
        response,
        operation,
        errorWhiteList,
        logicalUserErrorsMessages
      });
    }
  });

  let headers = new HttpHeaders({
    'Timezone': TIMEZONE_NAME_OR_OFFSET,
    ...customHeaders ? customHeaders : {}
  });

  if (token) {
    headers = headers.set('Authorization', `${tokenPrefix} ${token}`);
    // triggers processes
    headers = headers.set('pipeliner-revision-flags', `2`);
  }

  if (appId) {
    headers = headers.set('Pipeliner-App-Id', appId);
  }

  headers = headers.set('x-short-wait-locks', `True`);

  if (version) {
    headers = headers.set('Pipeliner-App-Version', version);
  }

  const linkOptions: HttpLink.Options = {
    uri: operation => {
      const url = new URL(gqlUrl);

      if (operation.operationName) {
        url.searchParams.set('op', operation.operationName);
      }

      return url.toString();
    },
    headers,
    withCredentials: !isDevMode()
  };
  const link = httpLink.create(linkOptions);

  const links = createLinkChain
    ? createLinkChain({ defaultLink: { link, linkOptions } })
    : [link];

  return ApolloLink.from([
    errorLink,
    ...links
  ]);
};

export function hasAnyErrorCode(context: { graphQLErrors?: GraphQLError[]; }) {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (!graphQLErrors || !graphQLErrors.length) {
    return false;
  } else {
    return !!graphQLErrors
      .filter((error: any) => !!error.api_error)
      .map((error: any) => error.api_error as ApiError)
      .find(error => !!error.code);
  }
}

export function retrieveAPIErrors(context: { graphQLErrors?: GraphQLError[]; }): ApiError[] {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (!graphQLErrors || !graphQLErrors.length) {
    return [];
  } else {
    return graphQLErrors
      .filter((error: any) => !!error.api_error && !!(error.api_error as ApiError).code)
      .map((error: any) => (error.api_error as ApiError));
  }
}

export function retrieveErrorCodes(context: { graphQLErrors?: GraphQLError[]; }): ErrorCode[] {
  return retrieveAPIErrors(context).map(apiError => apiError.code);
}

type DiscriminateUnion<T, K extends keyof T, V extends T[K]> = T extends Record<K, V> ? T : never;
type ApiErrorsMap = { [V in ApiError['code']]: DiscriminateUnion<ApiError, 'code', V> };

export function retrieveSpecificErrors<K extends ErrorCode>(context: { graphQLErrors?: (GraphQLError & { api_error?: ApiError; })[]; }, errorCode: K): ApiErrorsMap[K][] {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (!graphQLErrors || !graphQLErrors.length) {
    return [];
  }

  return graphQLErrors
    .filter((error: any) => !!error.api_error && error.api_error.code === errorCode)
    .map((error: any) => error.api_error) as ApiErrorsMap[K][];
}

export function retrieveFirstErrorCode(context: { graphQLErrors?: GraphQLError[]; }): ErrorCode {
  const errors = retrieveErrorCodes(context);
  return errors && !!errors.length ? errors[0] : null;
}

export function hasErrorCode(context: { graphQLErrors?: GraphQLError[]; }, errorCode: ErrorCode) {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (!graphQLErrors || !graphQLErrors.length) {
    return false;
  } else {
    return !!graphQLErrors
      .filter((error: any) => !!error.api_error)
      .map((error: any) => error.api_error as ApiError)
      .find(error => error.code === errorCode);
  }
}

export function retrieveFirstError(
  context: { graphQLErrors?: (GraphQLError & { api_error?: ApiError; })[]; },
  errorCode: ErrorCode
): ApiError | null {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (graphQLErrors && graphQLErrors.length) {
    const foundApiError = graphQLErrors
      .filter((error: any) => !!error.api_error)
      .map((error: any) => error.api_error as ApiError)
      .find(apiError => apiError.code === errorCode);
    return foundApiError || null;
  } else {
    return null;
  }
}

export function hasEntityValidationError(context: { graphQLErrors?: GraphQLError[]; }, errorCode: ErrorCode) {
  const graphQLErrors = getApolloGraphQLErrors(context);

  const entityValidationError = graphQLErrors
    && graphQLErrors.filter((error: any) => !!error.api_error)
      .map((error: any) => error.api_error as ApiError)
      .find(error => String(error.code) === String(ErrorCode.ERROR_ENTITY_VALIDATION));

  if (entityValidationError) {
    return !!(entityValidationError as EntityValidationException).entity_errors
      .map(error => error.code)
      .find(code => String(code) === String(errorCode));
  } else {
    return false;
  }
}

export function hasInvalidConditionErrorCode(context: { graphQLErrors?: GraphQLError[]; }) {
  return hasErrorCode(context, ErrorCode.ERROR_FIELD_ACCESS_INVALID_CONDITION);
}

export function hasErrorCodes(context: { graphQLErrors?: GraphQLError[]; }, errorCodes: ErrorCode[]) {
  const graphQLErrors = getApolloGraphQLErrors(context);

  if (!graphQLErrors || !graphQLErrors.length) {
    return false;
  } else {
    return !!graphQLErrors
      .filter((error: any) => !!error.api_error)
      .map((error: any) => error.api_error as ApiError)
      .find(error => errorCodes.includes(error.code));
  }
}

export function getErrorMessage(error: GraphQLError & { api_error?: ApiError; }) {
  return error && error.api_error && error.api_error.message
    ? error.api_error.message
    : error.message;
}

export function handleEntityValidationErrors(
  error: { graphQLErrors?: (GraphQLError & { api_error?: ApiError; })[]; },
  errorHandlers: { [key in ErrorCode]?: (entityError: ApiErrorBase) => typeof EMPTY }
): typeof EMPTY {
  if (hasErrorCode(error, ErrorCode.ERROR_ENTITY_VALIDATION)) {
    const entityValidationError = retrieveFirstError(error, ErrorCode.ERROR_ENTITY_VALIDATION);
    for (const entityError of (entityValidationError as EntityValidationException).entity_errors) {
      if (entityError.code in errorHandlers) {
        return errorHandlers[entityError.code](entityError);
      }
    }
  }
  return throwError(error);
}

export interface CreateLinkChainDefaults {
  defaultLink: LinkInfo;
}
